// src/pages/perfilAtleta.jsx - Versión corregida
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPlayerById, getFlagUrl } from '../services/athleteDataService';

const PerfilAtleta = () => {
  const { id } = useParams();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  // Estado para el video seleccionado actualmente
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  useEffect(() => {
    try {
      const playerData = getPlayerById(id);
      console.log("Datos del jugador:", playerData);
      setAthlete(playerData);
      setLoading(false);
      
      // Scroll to top when loading a new profile
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error al cargar datos del atleta:', error);
      setLoading(false);
    }
  }, [id]);

  // Resetear el índice de video al cambiar de atleta
  useEffect(() => {
    setSelectedVideoIndex(0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <div className="w-24 h-24 border-t-4 border-r-4 border-red-600 rounded-full animate-spin"></div>
        <p className="mt-6 text-xl">Cargando perfil...</p>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <div className="text-center max-w-md mx-auto px-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 text-red-600 mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h1 className="text-3xl font-bold mb-4">Atleta no encontrado</h1>
          <p className="text-gray-400 mb-8">No pudimos encontrar el perfil de este atleta. Es posible que haya sido trasladado o eliminado.</p>
          <Link to="/atletas" className="px-6 py-3 bg-red-600 rounded-full text-white font-bold hover:bg-red-700 transition-colors">
            Volver a la lista de atletas
          </Link>
        </div>
      </div>
    );
  }

  // Función auxiliar para transformar URLs de YouTube en URLs embebidas
  const getYouTubeEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;
    
    // Manejar formatos de URL de YouTube comunes
    let videoId = '';
    
    try {
      if (videoUrl.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(videoUrl.split('?')[1] || '');
        videoId = urlParams.get('v');
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1];
        // Eliminar parámetros adicionales si existen
        if (videoId && videoId.includes('?')) {
          videoId = videoId.split('?')[0];
        }
      }
      
      if (videoId) {
        // Usamos el dominio youtube-nocookie.com para mayor privacidad
        return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&showinfo=0`;
      }
    } catch (error) {
      console.error('Error al procesar URL de YouTube:', error);
    }
    
    return null; // Si no se pudo extraer el ID o hubo un error
  };

  // Función para obtener la miniatura de un video de YouTube
  const getYouTubeThumbnail = (videoUrl) => {
    if (!videoUrl) return null;
    
    let videoId = '';
    
    try {
      if (videoUrl.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(videoUrl.split('?')[1] || '');
        videoId = urlParams.get('v');
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1];
        if (videoId && videoId.includes('?')) {
          videoId = videoId.split('?')[0];
        }
      }
      
      if (videoId) {
        // Retorna la URL de la miniatura de calidad media
        return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      }
    } catch (error) {
      console.error('Error al procesar thumbnail de YouTube:', error);
    }
    
    return null;
  };

  // Renderizar banderas
  const renderFlags = () => {
    return (
      <div className="flex items-center space-x-3">
        {athlete.countryCode && (
          <div className="flex items-center">
            <img 
              src={getFlagUrl(athlete.countryCode)} 
              alt={`Bandera de ${athlete.country}`}
              className="h-6 w-auto object-cover rounded-sm"
            />
            <span className="ml-2 text-sm text-gray-300">{athlete.country}</span>
          </div>
        )}
        {athlete.secondCountryCode && (
          <div className="flex items-center">
            <img 
              src={getFlagUrl(athlete.secondCountryCode)}
              alt={`Segunda nacionalidad`}
              className="h-6 w-auto object-cover rounded-sm"
            />
            <span className="ml-2 text-sm text-gray-300">{athlete.secondCountry}</span>
          </div>
        )}
      </div>
    );
  };

  // Renderizar campo de fútbol con posición del jugador
  const renderFootballField = () => {
    // Determinar la posición en el campo basado en la posición del jugador
    let positionClass = "";
    let positionLabel = "";
    
    if (athlete.position) {
      const position = athlete.position.toLowerCase();
      const specificPosition = athlete.specificPosition ? athlete.specificPosition.toLowerCase() : "";
      
      if (position.includes("portero")) {
        positionClass = "top-[10%] left-[50%]";
        positionLabel = "POR";
      } else if (position.includes("defensa") || specificPosition.includes("defensa")) {
        if (specificPosition.includes("lateral izquierdo")) {
          positionClass = "top-[30%] left-[20%]";
          positionLabel = "LI";
        } else if (specificPosition.includes("lateral derecho")) {
          positionClass = "top-[30%] right-[20%]";
          positionLabel = "LD";
        } else if (specificPosition.includes("central")) {
          positionClass = "top-[30%] left-[50%]";
          positionLabel = "DFC";
        } else {
          positionClass = "top-[30%] left-[50%]";
          positionLabel = "DEF";
        }
      } else if (position.includes("medio") || specificPosition.includes("medio")) {
        if (specificPosition.includes("pivote")) {
          positionClass = "top-[50%] left-[50%]";
          positionLabel = "PIV";
        } else if (specificPosition.includes("ofensivo")) {
          positionClass = "top-[60%] left-[50%]";
          positionLabel = "MOF";
        } else if (specificPosition.includes("izquierdo") || specificPosition.includes("izquierda")) {
          positionClass = "top-[50%] left-[20%]";
          positionLabel = "MI";
        } else if (specificPosition.includes("derecho") || specificPosition.includes("derecha")) {
          positionClass = "top-[50%] right-[20%]";
          positionLabel = "MD";
        } else {
          positionClass = "top-[50%] left-[50%]";
          positionLabel = "MC";
        }
      } else if (position.includes("delantero") || specificPosition.includes("delantero")) {
        if (specificPosition.includes("centro")) {
          positionClass = "top-[80%] left-[50%]";
          positionLabel = "DC";
        } else if (specificPosition.includes("izquierdo") || specificPosition.includes("izquierda")) {
          positionClass = "top-[75%] left-[20%]";
          positionLabel = "EI";
        } else if (specificPosition.includes("derecho") || specificPosition.includes("derecha")) {
          positionClass = "top-[75%] right-[20%]";
          positionLabel = "ED";
        } else {
          positionClass = "top-[80%] left-[50%]";
          positionLabel = "DEL";
        }
      } else {
        // Default position (center)
        positionClass = "top-[50%] left-[50%]";
        positionLabel = "MC";
      }
    } else {
      // Default position (center)
      positionClass = "top-[50%] left-[50%]";
      positionLabel = "JUG";
    }
    
    return (
      <div className="relative w-full max-w-sm mx-auto aspect-[4/3] bg-gradient-to-b from-green-800 to-green-700 rounded-xl overflow-hidden mb-8 mt-8">
        {/* Líneas del campo */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Línea del centro */}
          <div className="absolute w-full h-[2px] bg-white opacity-70"></div>
          
          {/* Círculo central */}
          <div className="absolute w-24 h-24 rounded-full border-2 border-white opacity-70"></div>
          
          {/* Áreas */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-48 h-24 border-2 border-white opacity-70 border-t-0"></div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-24 border-2 border-white opacity-70 border-b-0"></div>
          
          {/* Puntos de penalti */}
          <div className="absolute top-[15%] left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full bg-white opacity-70"></div>
          <div className="absolute bottom-[15%] left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full bg-white opacity-70"></div>
        </div>
        
        {/* Posición del jugador */}
        <div className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${positionClass}`}>
          <div className="relative">
            <div className="relative z-10 flex items-center justify-center w-14 h-14 rounded-full bg-red-600 text-white font-bold border-4 border-white shadow-lg">
              {athlete.number || ""}
            </div>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-black text-xs px-2 py-1 rounded-full text-white font-bold shadow-md">
              {positionLabel}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Datos de estadísticas del jugador 
  const stats = athlete.stats || {
    matches: 0,
    goals: 0,
    assists: 0,
    minutes: 0,
    yellowCards: 0,
    redCards: 0,
    cleanSheets: 0,
    tackles: 0,
    saves: 0,
    passAccuracy: 0
  };
  // Renderizar contenido de la pestaña Highlights
  const renderHighlightsTab = () => {
    // Verificamos si hay videos disponibles
    const getVideoArray = () => {
      if (athlete.videos && Array.isArray(athlete.videos) && athlete.videos.length > 0) {
        return athlete.videos;
      } else if (athlete.video && Array.isArray(athlete.video) && athlete.video.length > 0) {
        return athlete.video;
      } else if (athlete.video && typeof athlete.video === 'string') {
        return [athlete.video];
      } else if (athlete.highlightsVideos) {
        if (athlete.highlightsVideos.others && Array.isArray(athlete.highlightsVideos.others)) {
          return [athlete.highlightsVideos.main, ...athlete.highlightsVideos.others].filter(Boolean);
        } else if (athlete.highlightsVideos.main) {
          return [athlete.highlightsVideos.main];
        }
      }
      return [];
    };
    
    const videoArray = getVideoArray();
    const hasVideos = videoArray.length > 0;
    
    console.log("Videos disponibles:", videoArray);
    
    return (
      <div>
        <h3 className="text-xl font-bold mb-6">Highlights de {athlete.name}</h3>
        
        {hasVideos ? (
          <div className="mb-8">
            <div className="aspect-video w-full overflow-hidden rounded-xl bg-gray-900/60 backdrop-blur-sm">
              {/* Reproducimos el video seleccionado actualmente */}
              {getYouTubeEmbedUrl(videoArray[selectedVideoIndex]) ? (
                <iframe 
                  className="w-full h-full" 
                  src={getYouTubeEmbedUrl(videoArray[selectedVideoIndex])}
                  title={`Video highlights de ${athlete.name}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gray-800">
                  <p className="text-gray-400 px-4 text-center">Error al cargar el video. El formato de URL no es compatible o la URL no es válida.</p>
                </div>
              )}
            </div>
            <div className="mt-4 text-gray-300">
              <h4 className="text-lg font-medium mb-2">Mejores momentos: {athlete.name}</h4>
              <p className="text-sm text-gray-400">
                {athlete.videoDescription || `Disfruta de los mejores momentos de ${athlete.name} con ${athlete.team}. Habilidad, talento y pasión por el fútbol.`}
              </p>
            </div>
            
            {/* Si hay múltiples videos, mostramos thumbnails para seleccionar */}
            {videoArray.length > 1 && (
              <div className="mt-6">
                <h5 className="text-sm font-medium text-gray-400 mb-3">Más videos</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {videoArray.map((videoUrl, index) => (
                    <div 
                      key={index} 
                      className={`relative aspect-video bg-gray-800 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity ${selectedVideoIndex === index ? 'ring-2 ring-red-600' : ''}`}
                      onClick={() => setSelectedVideoIndex(index)}
                    >
                      {/* Mostramos thumbnail si está disponible */}
                      {getYouTubeThumbnail(videoUrl) ? (
                        <img 
                          src={getYouTubeThumbnail(videoUrl)} 
                          alt={`Thumbnail video ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <span className="text-white text-xs">Video {index + 1}</span>
                        </div>
                      )}
                      
                      {/* Indicador de video seleccionado */}
                      {selectedVideoIndex === index && (
                        <div className="absolute inset-0 bg-red-600/20 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 bg-gray-900/60 rounded-xl text-center backdrop-blur-sm mb-8">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-medium mb-4">No hay videos disponibles</h3>
            <p className="text-gray-400 mb-6">Actualmente no hay videos de highlights disponibles para este jugador.</p>
          </div>
        )}
        
        {/* Stats del jugador */}
        <div className="mt-12">
          <h3 className="text-xl font-bold mb-4">Estadísticas destacadas</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-900/60 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.matches}</div>
              <div className="text-sm text-gray-400">Partidos</div>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.goals}</div>
              <div className="text-sm text-gray-400">Goles</div>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.assists}</div>
              <div className="text-sm text-gray-400">Asistencias</div>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.minutesPlayed ? Math.floor(stats.minutesPlayed / 60) : 0}</div>
              <div className="text-sm text-gray-400">Horas jugadas</div>
            </div>
          </div>
        </div>
        
        {/* Competiciones en las que ha participado */}
        {athlete.competitions && athlete.competitions.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xl font-bold mb-4">Competiciones</h3>
            <div className="flex flex-wrap gap-2">
              {athlete.competitions.map((competition, index) => (
                <span key={index} className="bg-gray-800 px-3 py-1 rounded-full text-sm">{competition}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Renderizar contenido según la pestaña activa
  const renderTabContent = () => {
    switch (activeTab) {
      case 'stats':
        return (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                <div className="text-4xl font-bold text-red-600 mb-1">{stats.matches}</div>
                <div className="text-sm text-gray-400">Partidos jugados</div>
              </div>
              
              <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                <div className="text-4xl font-bold text-red-600 mb-1">{stats.goals}</div>
                <div className="text-sm text-gray-400">Goles anotados</div>
              </div>
              
              <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                <div className="text-4xl font-bold text-red-600 mb-1">{stats.assists}</div>
                <div className="text-sm text-gray-400">Asistencias</div>
              </div>
              
              <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                <div className="text-4xl font-bold text-red-600 mb-1">{stats.minutesPlayed || 0}</div>
                <div className="text-sm text-gray-400">Minutos jugados</div>
              </div>
              
              {/* Estadísticas adicionales según la posición del jugador */}
              {athlete.position && athlete.position.toLowerCase().includes('portero') ? (
                <>
                  <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                    <div className="text-4xl font-bold text-red-600 mb-1">{stats.cleanSheets || 0}</div>
                    <div className="text-sm text-gray-400">Porterías a cero</div>
                  </div>
                  <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                    <div className="text-4xl font-bold text-red-600 mb-1">{stats.savePercentage || 0}%</div>
                    <div className="text-sm text-gray-400">% Paradas</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                    <div className="text-4xl font-bold text-red-600 mb-1">{stats.yellowCards || 0}</div>
                    <div className="text-sm text-gray-400">Tarjetas amarillas</div>
                  </div>
                  <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                    <div className="text-4xl font-bold text-red-600 mb-1">{stats.redCards || 0}</div>
                    <div className="text-sm text-gray-400">Tarjetas rojas</div>
                  </div>
                  <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                    <div className="text-4xl font-bold text-red-600 mb-1">{stats.passAccuracy || 0}%</div>
                    <div className="text-sm text-gray-400">Precisión de pases</div>
                  </div>
                </>
              )}
            </div>
            
            {/* Campo de fútbol con posición del jugador */}
            <div className="mt-16">
              <h3 className="text-xl font-bold mb-6 text-center">Posición en el campo</h3>
              {renderFootballField()}
            </div>
          </div>
        );
        
      case 'career':
        return (
          <div>
            <h3 className="text-xl font-bold mb-6">Trayectoria</h3>
            {athlete.careerHistory && athlete.careerHistory.length > 0 ? (
              <div className="space-y-6">
                {athlete.careerHistory.map((item, index) => (
                  <div key={index} className="relative pl-8 pb-6 border-l-2 border-gray-800">
                    <div className="absolute left-[-8px] top-0 w-4 h-4 bg-red-600 rounded-full"></div>
                    <div className="text-sm text-gray-400 mb-1">{item.season}</div>
                    <div className="text-lg font-bold">{item.club}</div>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {item.value && item.value !== "-" && (
                        <span className="text-sm bg-gray-800 px-2 py-1 rounded">Valor: {item.value}</span>
                      )}
                      {item.transferType && item.transferType !== "-" && (
                        <span className="text-sm bg-gray-800 px-2 py-1 rounded">{item.transferType}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No hay datos de trayectoria disponibles para este jugador.</p>
            )}
            
            {athlete.highlights && athlete.highlights.length > 0 && (
              <>
                <h3 className="text-xl font-bold mt-12 mb-6">Logros destacados</h3>
                <div className="space-y-4">
                  {athlete.highlights.map((item, index) => (
                    <div key={index} className="flex items-center px-4 py-3 bg-gray-900/60 rounded-lg backdrop-blur-sm">
                      <div className="h-10 w-10 rounded-full bg-red-600/20 flex items-center justify-center mr-4 text-red-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium">{item}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );
        
        case 'media':
        return (
          <div>
            <h3 className="text-xl font-bold mb-6">Imágenes del jugador</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="group relative overflow-hidden rounded-lg">
                <img 
                  src={athlete.image} 
                  alt={athlete.name}
                  className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-4">
                    <div className="text-sm font-medium">{athlete.name} - {athlete.team}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Campo de fútbol con posición del jugador */}
            <div className="mt-16">
              <h3 className="text-xl font-bold mb-6 text-center">Posición en el campo</h3>
              {renderFootballField()}
            </div>
            
            <div className="mt-16 p-8 bg-gray-900/60 rounded-xl text-center backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-2">¿Necesitas más contenido multimedia?</h3>
              <p className="text-gray-300 mb-6">Contacta con nosotros para solicitar más fotografías y videos de {athlete.name}</p>
              <a 
                href="/contacto" 
                className="px-6 py-3 bg-red-600 rounded-full text-white font-bold transition-colors hover:bg-red-700"
              >
                Contactar
              </a>
            </div>
          </div>
        );
      
      case 'highlights':
        return renderHighlightsTab();
        
      case 'info':
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h3 className="text-xl font-bold mb-4">Información personal</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Nombre completo</div>
                  <div className="font-medium">{athlete.fullName || athlete.name}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Edad</div>
                  <div className="font-medium">{athlete.age} años</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Fecha de nacimiento</div>
                  <div className="font-medium">{athlete.birthDate || 'No disponible'}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Lugar de nacimiento</div>
                  <div className="font-medium">{athlete.birthPlace || 'No disponible'}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Altura</div>
                  <div className="font-medium">{athlete.height || '1.80'}m</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Posición</div>
                  <div className="font-medium">{athlete.specificPosition || athlete.position}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Pie dominante</div>
                  <div className="font-medium">{athlete.foot || 'No especificado'}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Dorsal</div>
                  <div className="font-medium">#{athlete.number || '10'}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Nacionalidad</div>
                  <div className="font-medium">{athlete.country}</div>
                </div>
              </div>
              
              {/* Campo de fútbol con posición del jugador */}
              <div className="mt-12">
                <h3 className="text-xl font-bold mb-4">Posición en el campo</h3>
                {renderFootballField()}
              </div>
              
              {athlete.contract && (
                <div className="mt-12">
                  <h3 className="text-xl font-bold mb-4">Detalles de contrato</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
                    {athlete.contract.signed && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Firmado</div>
                        <div className="font-medium">{athlete.contract.signed}</div>
                      </div>
                    )}
                    
                    {athlete.contract.until && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Válido hasta</div>
                        <div className="font-medium">{athlete.contract.until}</div>
                      </div>
                    )}
                    
                    {athlete.contract.lastRenewal && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Última renovación</div>
                        <div className="font-medium">{athlete.contract.lastRenewal}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {athlete.marketValue && (
                <div className="mt-8">
                  <h4 className="font-bold mb-3">Valor de mercado</h4>
                  <div className="flex items-center bg-gray-900/60 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-600 mr-3">{athlete.marketValue}</div>
                    {athlete.marketValueHighest && (
                      <div className="text-sm text-gray-400">Valor máximo: {athlete.marketValueHighest}</div>
                    )}
                  </div>
                  
                  {(athlete.transfer || athlete.transfermarktUrl || athlete.externalUrls?.transfermarkt) ? (
                    <div className="mt-2 text-right">
                      <a 
                        href={athlete.transfer || athlete.transfermarktUrl || athlete.externalUrls?.transfermarkt}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-500 hover:text-red-400 inline-flex items-center"
                      >
                        <span>Ver historial de valores</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                      </a>
                    </div>
                  ) : (
                    <div className="mt-2 text-right">
                      <a 
                        href={`https://www.transfermarkt.es/schnellsuche/ergebnis/schnellsuche?query=${encodeURIComponent(athlete.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-500 hover:text-red-400 inline-flex items-center"
                      >
                        <span>Buscar en Transfermarkt</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              )}
              
              {athlete.highlights && athlete.highlights.length > 0 && (
                <div className="mt-8">
                  <h4 className="font-bold mb-3">Competiciones</h4>
                  <div className="flex flex-wrap gap-2">
                    {athlete.highlights.map((highlight, index) => (
                      <span key={index} className="bg-gray-800 px-3 py-1 rounded-full text-sm">{highlight}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-gray-900/60 rounded-xl p-6 backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-4">Equipo actual</h3>
                <div className="flex items-center">
                  <div className="h-14 w-14 bg-gray-800 rounded-lg flex items-center justify-center text-center p-2 mr-4">
                    <div className="text-red-600 font-bold">
                      {athlete.team?.substring(0, 3) || 'APY'}
                    </div>
                  </div>
                  <div>
                    <div className="font-bold text-lg">{athlete.team}</div>
                    {athlete.contract && (
                      <div className="text-sm text-gray-400">Desde {athlete.contract.signed?.substring(0,4) || '2021'}</div>
                    )}
                  </div>
                </div>
                
                <h4 className="font-bold mt-6 mb-3">Representado por</h4>
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-black/50 rounded flex items-center justify-center mr-3">
                    <div className="text-red-600 font-bold text-sm">APY</div>
                  </div>
                  <div>
                    <div className="font-medium">APY GROUP</div>
                    <div className="text-xs text-gray-400">Representación deportiva</div>
                  </div>
                </div>
                
                {athlete.agent && (
                  <div className="mt-4 text-sm">
                    <span className="text-gray-400">Agente:</span> {athlete.agent}
                  </div>
                )}
              </div>
              
              <div className="mt-6 space-y-3">
                <a
                  href="/contacto"
                  className="block w-full py-3 bg-red-600 text-white text-center font-bold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Contactar sobre este atleta
                </a>
                
                {/* Botón de Transfermarkt - ahora siempre visible */}
                <a
                  href={athlete.transfer || athlete.transfermarktUrl || athlete.externalUrls?.transfermarkt || 
                        `https://www.transfermarkt.es/schnellsuche/ergebnis/schnellsuche?query=${encodeURIComponent(athlete.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-gray-800 text-white text-center font-bold rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  {(athlete.transfer || athlete.transfermarktUrl || athlete.externalUrls?.transfermarkt) ? 
                    "Ver en Transfermarkt" : "Buscar en Transfermarkt"}
                </a>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-16">
      {/* Hero Section - Con imagen de estadio como fondo y jugador flotante */}
      <section className="relative h-[45vh] md:h-[60vh] overflow-hidden">
        {/* Background con imagen del estadio */}
        <div className="absolute inset-0 bg-black">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url('https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/banner.png')`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              filter: 'blur(2px)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40"></div>
        </div>
        
        {/* Player Info con logo y nombre */}
        <div className="absolute inset-0 flex flex-col justify-end">
          <div className="container mx-auto px-4 pb-1 md:pb-12 relative">
            <div className="flex flex-col md:flex-row items-center md:items-end relative">
              {/* Escudo del jugador (optimizado para móvil) */}
              <div className="md:mr-8 -mt-16 mb-2 md:mb-0 md:mt-0 md:w-1/4 flex justify-center md:justify-start">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-16 h-16 border-2 border-red-600/50 rounded-lg z-0"></div>
                  <img 
                    src={athlete.image} 
                    alt={athlete.name} 
                    className="w-36 md:w-full max-w-[200px] aspect-[3/4] object-cover object-top rounded-xl relative z-10 shadow-xl shadow-black/50"
                  />
                  <div className="absolute top-4 right-4 bg-red-600 text-white font-bold text-xl rounded-full h-10 w-10 flex items-center justify-center shadow-lg z-20">
                    {athlete.number || '10'}
                  </div>
                </div>
              </div>
              
              {/* Información del jugador */}
              <div className="md:flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row items-center md:items-start mb-1">
                  <div className="flex items-center px-3 py-1 bg-red-600/20 backdrop-blur-sm rounded mr-3 mb-1 md:mb-0">
                    <span className="text-red-600 font-medium">{athlete.team}</span>
                  </div>
                  {renderFlags()}
                </div>
                
                <h1 className="text-2xl md:text-5xl font-black">{athlete.name}</h1>
                <p className="text-lg md:text-2xl text-gray-300 mt-1">{athlete.specificPosition || athlete.position}</p>
              </div>
              
              {/* Jugador flotante a la derecha (solo visible en desktop) */}
              <div className="hidden md:block absolute right-4 bottom-0 md:w-1/3 h-full">
                <img 
                  src={athlete.image2 || '/images/player-action.png'} 
                  alt={`${athlete.name} en acción`} 
                  className="h-full object-contain object-bottom animate-float"
                  style={{filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.7))'}}
                />
              </div>
            </div>
            
            {/* Breadcrumbs */}
            <div className="mt-1 md:mt-8 flex flex-wrap justify-center md:justify-start items-center text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Inicio</Link>
              <span className="mx-2 text-gray-600">/</span>
              <Link to="/atletas" className="text-gray-400 hover:text-white transition-colors">Atletas</Link>
              <span className="mx-2 text-gray-600">/</span>
              <span className="text-white">{athlete.name}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tabs Section - Ahora con tab de Highlights y optimizado para móvil */}
      <section className="py-2 md:py-8 border-b border-gray-800 sticky top-0 bg-black/80 backdrop-blur-sm z-30">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto hide-scrollbar space-x-2 md:space-x-8 pb-1">
            <button 
              onClick={() => setActiveTab('info')}
              className={`pb-2 px-1 whitespace-nowrap font-medium transition-colors ${
                activeTab === 'info' 
                  ? 'text-red-600 border-b-2 border-red-600' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Información
            </button>
            <button 
              onClick={() => setActiveTab('stats')}
              className={`pb-2 px-1 whitespace-nowrap font-medium transition-colors ${
                activeTab === 'stats' 
                  ? 'text-red-600 border-b-2 border-red-600' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Estadísticas
            </button>
            <button 
              onClick={() => setActiveTab('career')}
              className={`pb-2 px-1 whitespace-nowrap font-medium transition-colors ${
                activeTab === 'career' 
                  ? 'text-red-600 border-b-2 border-red-600' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Carrera
            </button>
            <button 
              onClick={() => setActiveTab('highlights')}
              className={`pb-2 px-1 whitespace-nowrap font-medium transition-colors ${
                activeTab === 'highlights' 
                  ? 'text-red-600 border-b-2 border-red-600' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Highlights
            </button>
            <button 
              onClick={() => setActiveTab('media')}
              className={`pb-2 px-1 whitespace-nowrap font-medium transition-colors ${
                activeTab === 'media' 
                  ? 'text-red-600 border-b-2 border-red-600' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Media
            </button>
          </div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-4 md:py-12">
        <div className="container mx-auto px-4">
          {renderTabContent()}
        </div>
      </section>
      
      {/* Call to Action - Optimizado para móvil */}
      <section className="mt-8">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-6 md:p-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-center lg:text-left">
                <h2 className="text-xl md:text-2xl font-bold mb-2">¿Interesado en este atleta?</h2>
                <p className="text-gray-400">Contacta con APY GROUP para más información sobre {athlete.name}</p>
              </div>
              <div className="flex flex-col md:flex-row w-full lg:w-auto space-y-3 md:space-y-0 md:space-x-4">
                <a 
                  href={`/contacto?subject=Interés en ${athlete.name}`} 
                  className="w-full md:w-auto text-center px-6 py-3 bg-red-600 text-white font-bold rounded-full hover:bg-red-700 transition-colors"
                >
                  Contactar
                </a>
                <Link 
                  to="/atletas" 
                  className="w-full md:w-auto text-center px-6 py-3 bg-gray-800 text-white font-bold rounded-full hover:bg-gray-700 transition-colors"
                >
                  Ver otros atletas
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CSS adicional para animaciones */}
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PerfilAtleta;